import { ExternalLink, Copy } from 'lucide-react'
import { useToast } from './ToastContext'

const WIN_LIBRARY_URL = 'https://marketinglmr.com/win-library/'

// Add or edit links here. Each section renders as a card; each link opens in a new tab.
// Optional: give a link `credentials: { username, password }` to show a shared
// login under it with copy buttons.
const SECTIONS = [
  {
    title: 'Client Wins',
    links: [
      {
        label: 'Win Library',
        note: 'Stuck on an account? Client wins, what your teammate changed, and what we learned. Pick the problem you have and see who has solved it.',
        url: WIN_LIBRARY_URL,
        credentials: { username: 'lmrteam', password: 'wxLD-ds8x-HFkn-eq7A' },
      },
    ],
  },
  {
    title: 'Guides & Training',
    links: [
      {
        label: 'Loom Video Library',
        note: 'Full how-to video index (Divi, Hubspot, Google Ads, SEO, Squarespace, and more)',
        url: 'https://docs.google.com/spreadsheets/d/19dKGFdb4UpeZfDowc0PDb5LKuzGC5pUmtMi5I8kDuP0/edit',
      },
      {
        label: 'Level Up Doc',
        note: 'Team level-up reference',
        url: 'https://docs.google.com/document/d/1mdXUic9asMKZg5JI_X-yGI-tIczY3nAchpGo6PifjIw/edit',
      },
    ],
  },
  {
    title: 'Google Ads',
    links: [
      {
        label: 'Google Ads Non-Compliance Rejection: Amazon Products',
        note: 'Use this expanded form to appeal, do not use in-platform appeal option.',
        url: 'https://support.google.com/google-ads/contact/approvals',
      },
      {
        label: 'Apply for Healthcare-Related Advertising (LegitScript)',
        note: 'Post the LegitScript certification task here to prove certification and unlock healthcare ad eligibility.',
        url: 'https://support.google.com/google-ads/troubleshooter/6099627',
      },
    ],
  },
  {
    title: 'AEO Resources',
    links: [
      {
        label: 'Loop Marketing Prompt Library',
        note: 'Prompt library for AEO / AI answer optimization',
        url: 'https://offers.hubspot.com/view/loop-marketing-prompts',
      },
      {
        label: 'People Also Ask',
        note: 'Research questions people ask around a topic',
        url: 'https://peoplealsoask.ai/',
      },
    ],
  },
]

// A written guide (not links). Renders as its own card. Each item is a pattern
// to AVOID so copy reads as human-written. Examples are kept verbatim.
const WRITING_GUIDE = {
  title: 'Writing Copy That Doesn’t Sound AI-Written',
  intro: 'Patterns to avoid so copy reads like a person wrote it. Each tell is harmless once; what gives AI away is using them as the default shape over and over.',
  items: [
    {
      name: `The "not X — it's Y" reversal`,
      body: `Real people occasionally flip a sentence like this; AI does it in every paragraph because it reliably sounds insightful. The repetition is what gives it away.`,
      examples: [
        `"It's not about trying harder. It's about getting you back."`,
        `"A late diagnosis isn't 'too late.' It's the first time the whole picture makes sense."`,
        `"not an afterthought"`,
        `"treated together rather than in isolation."`,
      ],
    },
    {
      name: `Rule-of-three stacking`,
      body: `Three balanced items with the last one slightly escalated. One is fine; using it as the default sentence shape across eight sections is a fingerprint.`,
      examples: [
        `"No referral, no maze, no guessing."`,
        `"your goals, your life, and your nervous system."`,
        `"over-preparing, people-pleasing, perfectionism."`,
        `"lists, alarms, and systems."`,
      ],
    },
    {
      name: `"actually" as the authenticity tic`,
      body: `It's trying to signal "this is the real thing, unlike the others," and it shows up exactly where the writing is reaching for credibility it hasn't earned with specifics.`,
      examples: [
        `"care that actually fits"`,
        `"how it actually shows up"`,
        `"how ADHD actually presents in women"`,
        `"support that actually fit how you live"`,
      ],
    },
    {
      name: `Scare-quoted relatable phrases`,
      body: `Dropping a quoted bit of inner monologue is a stock empathy device. A couple land; a dozen of them across the page reads like a formula for sounding warm.`,
      examples: [],
    },
    {
      name: `The "here's what…" pointer and "so often"`,
      body: `Connective-tissue phrases that add cadence but no information. They're pure scaffolding.`,
      examples: [
        `"Here's what it often feels like instead."`,
        `"Here's what's usually going on instead."`,
        `"here's exactly what to expect."`,
        `"the anxiety and depression that so often accompany"`,
        `"what so often comes with it"`,
      ],
    },
    {
      name: `The word "quietly"`,
      body: `Eliminate it entirely.`,
      examples: [],
    },
    {
      name: `Em dashes`,
      body: `Don't use them, or use them very sparingly.`,
      examples: [],
    },
  ],
}

function CredentialRow({ label, value, onCopy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      <code style={{ fontSize: 12.5, color: 'var(--text-primary)', background: 'var(--border-light)', padding: '3px 8px', borderRadius: 6, overflowWrap: 'anywhere' }}>{value}</code>
      <button
        type="button"
        onClick={() => onCopy(label, value)}
        aria-label={`Copy ${label.toLowerCase()}`}
        title={`Copy ${label.toLowerCase()}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}
      >
        <Copy size={14} /> Copy
      </button>
    </div>
  )
}

export default function ResourcesView() {
  const toast = useToast()

  function copyValue(label, value) {
    navigator.clipboard.writeText(value).then(
      () => toast(`${label} copied to clipboard`, 'success'),
      () => toast(`Couldn't copy. Select the ${label.toLowerCase()} and copy it manually.`, 'error'),
    )
  }

  return (
    <div>
      {SECTIONS.map(section => (
        <div key={section.title} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            {section.title}
          </div>
          <div className="card">
            {section.links.map((link, i) => (
              <div key={link.url} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border-light)' }}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    textDecoration: 'none', color: 'inherit',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{link.label}</div>
                    {link.note && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{link.note}</div>}
                  </div>
                  <ExternalLink size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                </a>
                {link.credentials && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', padding: '0 16px 12px' }}>
                    <CredentialRow label="Username" value={link.credentials.username} onCopy={copyValue} />
                    <CredentialRow label="Password" value={link.credentials.password} onCopy={copyValue} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
          {WRITING_GUIDE.title}
        </div>
        <div className="card" style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 14 }}>{WRITING_GUIDE.intro}</div>
          {WRITING_GUIDE.items.map((item, i) => (
            <div key={i} style={{ marginTop: i === 0 ? 0 : 14, paddingTop: i === 0 ? 0 : 14, borderTop: i === 0 ? 'none' : '1px solid var(--border-light)' }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                <span style={{ color: 'var(--urgent)' }}>✕ </span>{item.name}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.body}</div>
              {item.examples && item.examples.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {item.examples.map((ex, j) => (
                    <li key={j} style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 3 }}>{ex}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}